// =====================================================================
//  Onglet « Contrat d'appel » d'un template (lecture seule).
//  Documente, dans le BO, l'appel que l'application distante doit émettre
//  vers POST /process/run pour utiliser ce template : envoi de mail,
//  rendu HTML ou fichier PDF. Le ModelJson d'exemple est le modèle de
//  données de test du template (onglet « Modèle »).
//  Référence complète : docs/PROCESS_RUN_API.md.
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {ITemplateModel} from "../../Model/Template/ITemplateModel.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";
import {String} from "../../Tools/Misc.js";

/** Valeurs de TypeProcess acceptées par POST /process/run (PdfAndMail non implémenté). */
enum ContractProcess {
    Mail = 0,
    Html = 1,
    Pdf = 2,
}

const PROCESS_LABELS: Array<{ value: ContractProcess; label: string }> = [
    {value: ContractProcess.Mail, label: "Envoi de mail"},
    {value: ContractProcess.Html, label: "Rendu HTML"},
    {value: ContractProcess.Pdf, label: "Fichier PDF"},
];

export class TemplateContractEditor implements IDisposableLike {
    #jqMainContainer: JQuery | undefined;
    #jqProcessBar: JQuery | undefined;
    #jqProcessContent: JQuery | undefined;
    #model: ITemplateModel;
    #langages: Array<string>;
    #process: ContractProcess;

    constructor(options: ITemplateContractEditorOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#model = options.model;
        this.#langages = options.langages;
        // Le type de process du template présélectionne l'exemple ; PdfAndMail
        // (non implémenté côté API) retombe sur l'envoi de mail.
        this.#process = Object.values(ContractProcess).includes(options.model.TypeProcess)
            ? options.model.TypeProcess as ContractProcess
            : ContractProcess.Mail;
    }

    public Init = () => {
        this.#buildHtml();
    };

    get #isSaved(): boolean {
        return !!this.#model.Primarykey && this.#model.Primarykey !== String.GetEmptyGuid;
    }

    /** ModelJson d'exemple : le modèle de données de test du template, s'il est un JSON valide. */
    #modelJson = (): object => {
        const code = this.#model.Datas?.[0]?.Code;
        if (!code) return {};
        try {
            return JSON.parse(code);
        } catch {
            return {};
        }
    };

    #payloadExample = (): string => {
        const payload: any = {
            TypeProcess: this.#process,
            TemplateId: this.#isSaved ? this.#model.Primarykey : "(enregistrer pour obtenir l'Id)",
            Langage: "fr",
        };

        switch (this.#process) {
            case ContractProcess.Mail:
                payload.TypeMailProcess = 0;
                payload.Destinataires = [{Email: "destinataire@exemple.fr", Nom: "Camille DUPONT"}];
                // Sans expéditeur sur le template, l'appelant DOIT en fournir un.
                if (!this.#model.ExpediteurEmail)
                    payload.Expediteur = {Email: "no-reply@exemple.fr", Nom: "Ne pas répondre"};
                break;
            case ContractProcess.Pdf:
                payload.FileName = "document.pdf";
                break;
        }

        payload.ModelJson = this.#modelJson();

        if (this.#process === ContractProcess.Mail)
            payload.PiecesJointes = [{NomFichier: "CGU.pdf", TypeMime: "application/pdf", Contenu: "JVBERi0xLjQK..."}];

        return JSON.stringify(payload, null, 2);
    };

    #mailResponseExample = (): string => {
        const ret = {
            Data: {
                Primarykey: this.#isSaved ? this.#model.Primarykey : "...",
                SubjectContent: "Sujet fusionné",
                BodyContent: "<html>… corps fusionné …</html>",
            },
            Errors: [],
            HasError: false,
        };
        return JSON.stringify(ret, null, 2);
    };

    #buildHtml = () => {
        const wrap = $("<div></div>")
            .css({padding: "12px", maxWidth: "760px", overflowY: "auto", height: "100%", boxSizing: "border-box"})
            .appendTo(this.#jqMainContainer);

        $("<p></p>")
            .css({color: "#555", marginTop: 0})
            .html(
                "Ce contrat décrit l'appel que l'<b>application distante</b> doit émettre vers " +
                "<code>POST /process/run</code> pour utiliser ce template. Les données de fusion, " +
                "les destinataires et les pièces jointes sont fournis <b>à l'exécution</b> (ils ne sont pas configurés ici).",
            )
            .appendTo(wrap);

        // Bloc TemplateId
        const idRow = $("<div></div>")
            .css({display: "flex", alignItems: "center", gap: "8px", margin: "12px 0"})
            .appendTo(wrap);
        $("<label></label>").text("Identifiant du template").css({fontWeight: 600}).appendTo(idRow);
        const idBox = $("<code></code>")
            .text(this.#isSaved ? this.#model.Primarykey : "(non enregistré)")
            .css({background: "#f3f3f3", padding: "4px 8px", borderRadius: "4px", fontFamily: "monospace"})
            .appendTo(idRow);
        if (this.#isSaved)
            this.#copyButton("Copier", () => idBox.text(), "Id copié").appendTo(idRow);

        // Authentification + règles communes
        $("<p></p>")
            .css({color: "#555", marginTop: "12px"})
            .html(
                "<b>Authentification :</b> header <code>X-Account</code> portant le jeton d'un " +
                "<b>compte de service</b> (et non <code>Authorization</code>). " +
                "<code>TypeProcess</code> doit être envoyé en <b>entier</b> : c'est lui qui détermine le traitement.",
            )
            .appendTo(wrap);

        $("<p></p>")
            .css({color: "#555"})
            .html(
                "<b>Langues disponibles :</b> " +
                this.#langages.map(l => `<code>${l}</code>`).join(", ") +
                ". Le paramètre <code>Langage</code> est facultatif ; une langue absente retombe sur le pivot <code>fr</code>.",
            )
            .appendTo(wrap);

        // Sélecteur du type de process
        this.#jqProcessBar = $("<div></div>")
            .css({display: "flex", gap: "4px", margin: "16px 0 8px"})
            .appendTo(wrap);
        this.#jqProcessContent = $("<div></div>").appendTo(wrap);
        this.#renderProcess();

        $("<p></p>")
            .css({color: "#888", fontSize: "12px", marginTop: "20px"})
            .html("Référence complète : <code>docs/PROCESS_RUN_API.md</code>.")
            .appendTo(wrap);
    };

    #renderProcess = () => {
        this.#jqProcessBar.empty();
        for (const p of PROCESS_LABELS) {
            const active = p.value === this.#process;
            $("<button></button>")
                .addClass("k-button k-button-sm k-rounded-md")
                .addClass(active ? "k-button-solid k-button-solid-primary" : "k-button-outline k-button-outline-base")
                .text(`${p.label} (${p.value})`)
                .on("click", (e) => {
                    e.preventDefault();
                    this.#process = p.value;
                    this.#renderProcess();
                })
                .appendTo(this.#jqProcessBar);
        }

        const content = this.#jqProcessContent.empty();

        this.#title("Exemple de payload d'appel").appendTo(content);
        const pre = this.#codeBlock(this.#payloadExample()).appendTo(content);
        this.#copyButton("Copier le payload", () => pre.text(), "Payload copié")
            .css({marginTop: "8px"})
            .appendTo(content);

        $("<p></p>")
            .css({color: "#555", marginTop: "12px"})
            .html(
                "<code>ModelJson</code> doit contenir <b>toutes</b> les variables du template ; l'exemple " +
                "reprend le modèle de données de test (onglet « Modèle »). Dates au format ISO 8601 UTC " +
                "(<code>2026-06-10T12:00:00Z</code>).",
            )
            .appendTo(content);

        switch (this.#process) {
            case ContractProcess.Mail:
                this.#renderMail(content);
                break;
            case ContractProcess.Html:
                this.#renderResponse(content,
                    "EASYMAIL fusionne <b>le corps uniquement</b> et renvoie directement le contenu fusionné " +
                    "(<code>text/html</code>, ou <code>text/plain</code> pour un template texte). Aucun mail n'est envoyé.");
                break;
            case ContractProcess.Pdf:
                this.#renderResponse(content,
                    "EASYMAIL fusionne le corps et renvoie un <b>fichier PDF</b> (<code>application/pdf</code>) " +
                    "nommé d'après <code>FileName</code>. Aucun mail n'est envoyé.");
                break;
        }
    };

    #renderMail = (content: JQuery) => {
        $("<p></p>")
            .css({color: "#555"})
            .html(
                "<b>Expéditeur :</b> " +
                (this.#model.ExpediteurEmail
                    ? `facultatif. À défaut, celui du template est utilisé (<code>${kendo.htmlEncode(this.#model.ExpediteurEmail)}</code>).`
                    : "<b>obligatoire</b> : aucun expéditeur n'est défini sur ce template (onglet « Sujet »)."),
            )
            .appendTo(content);

        // Pièces jointes
        this.#title("Pièces jointes (facultatif)").css({marginTop: "16px"}).appendTo(content);
        $("<ul></ul>")
            .css({color: "#555", marginTop: 0, paddingLeft: "20px"})
            .html(
                "<li><code>NomFichier</code> : nom affiché chez le destinataire, sans chemin.</li>" +
                "<li><code>TypeMime</code> : facultatif, déduit de l'extension s'il est absent.</li>" +
                "<li><code>Contenu</code> : fichier encodé en <b>base64</b> brut (sans préfixe <code>data:...;base64,</code>).</li>" +
                "<li>Limites fixées par la configuration de l'API : par défaut <b>10 fichiers</b> et <b>10 Mo cumulés</b>.</li>" +
                "<li>Extensions exécutables refusées (<code>.exe</code>, <code>.js</code>, <code>.vbs</code>…).</li>",
            )
            .appendTo(content);

        // Réponse
        this.#title("Réponse").css({marginTop: "16px"}).appendTo(content);
        $("<p></p>")
            .css({color: "#555", marginTop: 0})
            .html(
                "JSON <code>ResponseApi</code>. Un échec (validation, pièce jointe refusée, envoi) peut " +
                "revenir en <code>200</code> : contrôlez <b>toujours</b> <code>HasError</code> / <code>Errors</code>.",
            )
            .appendTo(content);
        const preRet = this.#codeBlock(this.#mailResponseExample()).appendTo(content);
        this.#copyButton("Copier la réponse", () => preRet.text(), "Réponse copiée")
            .css({marginTop: "8px"})
            .appendTo(content);
    };

    #renderResponse = (content: JQuery, html: string) => {
        this.#title("Réponse").css({marginTop: "16px"}).appendTo(content);
        $("<p></p>").css({color: "#555", marginTop: 0}).html(html).appendTo(content);
    };

    #title = (text: string): JQuery =>
        $("<div></div>").text(text).css({fontWeight: 600, marginTop: "12px", marginBottom: "4px"});

    #codeBlock = (text: string): JQuery =>
        $("<pre></pre>")
            .css({
                background: "#1e1e1e",
                color: "#dcdcdc",
                padding: "14px",
                borderRadius: "6px",
                fontSize: "13px",
                overflowX: "auto",
            })
            .text(text);

    #copyButton = (label: string, text: () => string, notification: string): JQuery =>
        $("<button class='k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base'></button>")
            .text(label)
            .on("click", (e) => {
                e.preventDefault();
                navigator.clipboard
                    .writeText(text())
                    .then(() => NotificationHelper.Instance.success(notification));
            });

    public Dispose = () => {
        if (this.#jqMainContainer) this.#jqMainContainer.empty();
    };
}

export interface ITemplateContractEditorOptions {
    jqMainContainer: JQuery;
    /** Pivot du template (porte l'identité, l'expéditeur et le modèle de données de test). */
    model: ITemplateModel;
    /** Langues disponibles (pivot 'fr' + traductions). */
    langages: Array<string>;
}
